import { useEffect } from 'react';
import Checkbox from '@/Components/FormElements/Checkbox';
import GuestLayout from '@/Layouts/GuestLayout';
import InputError from '@/Components/FormElements/InputError';
import InputLabel from '@/Components/FormElements/InputLabel';
import PrimaryButton from '@/Components/Buttons/PrimaryButton';
import SecondaryButton from '@/Components/Buttons/SecondaryButton';
import TextInput from '@/Components/FormElements/TextInput';
import { Head, Link, useForm } from '@inertiajs/react';
import { usePasskeyVerify } from '@laravel/passkeys/react';

export default function Login({ status, canResetPassword }) {
    const { data, setData, post, processing, errors, reset } = useForm({
        email: '',
        password: '',
        remember: true,
    });

    const { verify, isLoading, error, isSupported } = usePasskeyVerify({
        autofill: true,
        remember: () => data.remember,
        onSuccess: (response) => {
            window.location.href = response.redirect ?? '/';
        },
    });

    useEffect(() => {
        return () => {
            reset('password');
        };
    }, []);

    const submit = (e) => {
        e.preventDefault();

        post(route('login'));
    };

    return (
        <GuestLayout>
            <Head title="Log in" />

            {status && <div className="mb-4 font-medium text-sm text-green-600">{status}</div>}

            {isSupported && (
                <>
                    <SecondaryButton
                        type="button"
                        className="w-full justify-center"
                        onClick={verify}
                        disabled={isLoading}
                    >
                        {isLoading ? 'Authenticating...' : 'Sign in with passkey'}
                    </SecondaryButton>
                    {error && <InputError message={error} className="mt-2 text-center" />}
                    <div className="relative my-6">
                        <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-gray-300" />
                        </div>
                        <div className="relative flex justify-center text-xs uppercase">
                            <span className="bg-white px-2 text-gray-500">Or continue with email</span>
                        </div>
                    </div>
                </>
            )}

            <form onSubmit={submit}>
                <div>
                    <InputLabel htmlFor="email" value="Email" />

                    <TextInput
                        id="email"
                        type="email"
                        name="email"
                        value={data.email}
                        className="mt-1 block w-full"
                        autoComplete="email webauthn"
                        isFocused={true}
                        onChange={(e) => setData('email', e.target.value)}
                    />

                    <InputError message={errors.email} className="mt-2" />
                </div>

                <div className="mt-4">
                    <InputLabel htmlFor="password" value="Password" />

                    <TextInput
                        id="password"
                        type="password"
                        name="password"
                        value={data.password}
                        className="mt-1 block w-full"
                        autoComplete="current-password"
                        onChange={(e) => setData('password', e.target.value)}
                    />

                    <InputError message={errors.password} className="mt-2" />
                </div>

                <div className="block mt-4">
                    <label className="flex items-center">
                        <Checkbox
                            name="remember"
                            checked={!data.remember}
                            onChange={(e) => setData('remember', !e.target.checked)}
                        />
                        <span className="ms-2 text-sm text-gray-600">Log me out after session</span>
                    </label>
                </div>

                <div className="flex items-center justify-between mt-4">
                    <Link
                        href={route('register')}
                        className="underline text-sm text-gray-600 hover:text-gray-900 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                        Register
                    </Link>
                    <div>
                        {canResetPassword && (
                            <Link
                                href={route('password.request')}
                                className="underline text-sm text-gray-600 hover:text-gray-900 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                            >
                                Forgot your password?
                            </Link>
                        )}

                        <PrimaryButton className="ms-4" disabled={processing}>
                            Log in
                    </PrimaryButton>
                    </div>
                </div>
            </form>
        </GuestLayout>
    );
}
